import React from 'react';
import { connect } from 'react-redux';
import { Input, Card, Button } from 'antd';
import styled from 'styled-components';
import { NativeButtonProps } from 'antd/lib/button/button';
import { DeleteOutlined } from '@ant-design/icons';

import { AutocompleteSelect } from 'features/pagination/components/AutocompleteSelect';
import { AutocompleteField } from 'features/pagination/models/paginationModel';
import { selectBenefitContract } from 'features/benefit/actions';
import {
    autocompleteRequest,
    clearInitialValueData,
    getInitialValueRequest,
} from 'features/pagination/actions/paginationActions';
import { selectContractOrganisation } from 'features/contract/actions/contractActions';
import { notArchivedFilterParam } from 'config/config';
import { ContractPriorities } from 'features/contract/models/contractModels';
import { BenefitPriority } from 'features/benefit/models/benefitModels';
import { Antd3FormProps } from 'common/components/deprecated/antd3';
import { colors } from 'theme/variables';

const PriorityRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #f2f4f7;

    &:last-child {
        border-bottom: none;
    }
`;

const PriorityInputWrapper = styled.div`
    flex: 1;

    .priority-name {
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 0.85rem;
        color: #3a4555;
        margin-bottom: 6px;
    }
`;

const RemoveBtn = styled((props: NativeButtonProps) => <Button {...props} />)`
    &&& {
        margin-top: 22px;
        border-radius: 8px;
        font-family: 'Inter', sans-serif;
        font-size: 0.8rem;
        height: 36px;
        display: flex;
        align-items: center;
        gap: 4px;
    }
`;

const StyledCard = styled(Card)`
    &&& {
        border-radius: 10px;
        border-color: #e8ecf2;

        .ant-card-body {
            padding: 16px 20px;
        }
    }
`;

interface AutocompleteDispatchProps {
    autocompleteRequest: typeof autocompleteRequest;
    getInitialValueRequest: typeof getInitialValueRequest;
    clearInitialValueData: typeof clearInitialValueData;
    selectContractOrganisation: typeof selectContractOrganisation;
    selectBenefitContract: typeof selectBenefitContract;
}

interface AutocompleteSelectionProps {
    selectNewPriority: (priorityData: ContractPriorities) => void;
    deletePriority: (priorityID: number) => void;
    changePriorityValue: (priorityID: number, value: number) => void;
    selectedPriorities: ContractPriorities[] | BenefitPriority[] | undefined;
    autocompleteField: AutocompleteField;
    id: string;
    placeholder: string;
    notFoundContent: string;
    tooltip?: string;
}

class PrioritiesFields extends React.Component<
    AutocompleteSelectionProps & AutocompleteDispatchProps & Antd3FormProps
> {
    private onDeselectSelection = (value: number) => {
        this.props.deletePriority(value);
    };

    public render(): JSX.Element {
        const {
            form,
            selectedPriorities,
            changePriorityValue,
            selectNewPriority,
            autocompleteField,
            id,
            placeholder,
            notFoundContent,
            tooltip,
        } = this.props;

        const prioritiesValues =
            selectedPriorities && selectedPriorities.length ? (
                <>
                    {selectedPriorities.map(({ priority, priorityName, id, value }) => (
                        <PriorityRow key={id}>
                            <PriorityInputWrapper>
                                <div className="priority-name">
                                    {priority?.name || priorityName}
                                </div>
                                <Input
                                    id="value"
                                    placeholder={`${priority?.name || priorityName} value`}
                                    type="number"
                                    onChange={(e) =>
                                        changePriorityValue(id, Number(e.target.value))
                                    }
                                    value={value}
                                />
                            </PriorityInputWrapper>

                            <RemoveBtn
                                size="small"
                                type="danger"
                                ghost
                                onClick={() => this.onDeselectSelection(id)}
                            >
                                <DeleteOutlined />
                                Remove
                            </RemoveBtn>
                        </PriorityRow>
                    ))}
                </>
            ) : null;

        return (
            <StyledCard>
                <AutocompleteSelect
                    form={form}
                    autocompleteField={autocompleteField}
                    id={id}
                    placeholder={placeholder}
                    notFoundContent={notFoundContent}
                    searchParams={notArchivedFilterParam}
                    onDeselectSelection={this.onDeselectSelection}
                    getAutocompleteData={selectNewPriority}
                    required={false}
                    tooltip={tooltip}
                />
                {prioritiesValues}
            </StyledCard>
        );
    }
}

export const PrioritiesForm = connect(null, {
    autocompleteRequest,
    getInitialValueRequest,
    clearInitialValueData,
    selectContractOrganisation,
    selectBenefitContract,
})(PrioritiesFields);
